import { useCallback, useEffect, useRef } from 'react'
import {
  playConnectingTone,
  playDialTone,
  playDialToneUrl,
  playRingtone,
  stopRingtone,
  unlockAudioOnGesture,
} from '../services/audio-manager'
import { getCachedRingerState, getRingerState, subscribeRingerState } from '../services/ringer-mode'
import { prepareNativeRingback } from '../services/voice-call/native-audio-route'
import { callAudioRouteArbiter } from '../utils/call-audio-route-arbiter'
import { getRingtonePreference } from '../utils/ringtone-preference'
import { isNativeApp } from '../utils/platform'
import { shouldPlayInAppRingtone, shouldVibrateForCall } from '../utils/ringer-policy'

export type CallRingtoneMode = 'incoming' | 'outgoing' | 'connecting'

export type CallRingtoneStartOptions = {
  /** Resolved dial/connecting URL (org custom). Omit → built-in asset. */
  url?: string | null
  /** Custom dial: play this many full takes (then stop). 0/undefined + no url → loop default. */
  repeatCount?: number
  onDialExhausted?: () => void
  /**
   * Force restart even when already in this mode (e.g. swap built-in → org URL,
   * or re-assert after CallKit/Chime reconfigured the audio session).
   */
  force?: boolean
}

const INCOMING_VIBRATE_PATTERN = [400, 200, 400, 200, 400, 800]

function canVibrate(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'
}

/** navigator.vibrate is unreliable/disabled in the Android WebView — use Haptics. */
function pulseNativeVibration(): void {
  void import('@capacitor/haptics')
    .then(({ Haptics }) => Haptics.vibrate({ duration: 1200 }))
    .catch(() => {
      /* haptics plugin unavailable */
    })
}

function pulseIncomingVibration(): void {
  if (isNativeApp()) {
    pulseNativeVibration()
    return
  }
  if (!canVibrate()) return
  try {
    navigator.vibrate(INCOMING_VIBRATE_PATTERN)
  } catch {
    /* unsupported */
  }
}

function stopVibration(): void {
  if (!canVibrate()) return
  try {
    navigator.vibrate(0)
  } catch {
    /* ignore */
  }
}

/** Looping ring / ringback via centralized Audio Manager. */
export function useCallRingtone() {
  const modeRef = useRef<CallRingtoneMode | null>(null)
  const vibrateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const ringerUnsubRef = useRef<(() => void) | null>(null)
  /** Bumped on every start/stop so a slow ringer-state read can't start a stale ring. */
  const ringSeqRef = useRef(0)

  const clearVibrateTimer = useCallback(() => {
    ringSeqRef.current += 1
    if (vibrateTimerRef.current) {
      clearInterval(vibrateTimerRef.current)
      vibrateTimerRef.current = null
    }
    ringerUnsubRef.current?.()
    ringerUnsubRef.current = null
    stopVibration()
  }, [])

  const stop = useCallback(() => {
    modeRef.current = null
    clearVibrateTimer()
    stopRingtone()
  }, [clearVibrateTimer])

  const start = useCallback(
    (mode: CallRingtoneMode, speakerOn = false, options?: CallRingtoneStartOptions) => {
      if (modeRef.current === mode && !options?.force) return
      modeRef.current = mode
      clearVibrateTimer()

      const startIncomingAlerts = async () => {
        const seq = ringSeqRef.current
        const state = await getRingerState()
        if (modeRef.current !== mode || seq !== ringSeqRef.current) return
        if (shouldPlayInAppRingtone(state)) {
          playRingtone(getRingtonePreference())
        }
        if (shouldVibrateForCall(state)) {
          pulseIncomingVibration()
          vibrateTimerRef.current = setInterval(() => {
            // Re-check each pulse: the user may flip to Silent mid-ring.
            if (shouldVibrateForCall(getCachedRingerState())) pulseIncomingVibration()
          }, 3200)
        }
        // Silencing the phone mid-ring silences the in-app ringtone too.
        ringerUnsubRef.current?.()
        ringerUnsubRef.current = subscribeRingerState((next) => {
          if (modeRef.current !== mode) return
          if (!shouldPlayInAppRingtone(next)) stopRingtone()
          if (!shouldVibrateForCall(next)) stopVibration()
        })
      }

      const ring = () => {
        if (modeRef.current !== mode) return
        if (mode === 'incoming') {
          // Callee hears their personal ringtone — never org dial / ringback
          // (that is outgoing-only for the caller) — unless the phone is on
          // Silent / DND (Android). Vibration still follows Vibrate mode.
          // Native Telecom / CallKit ringing is OS-governed and untouched.
          void startIncomingAlerts()
        } else if (mode === 'connecting') {
          playConnectingTone(options?.url ?? undefined)
        } else if (options?.url && (options.repeatCount ?? 0) > 0) {
          playDialToneUrl(options.url, {
            repeatCount: options.repeatCount,
            onExhausted: options.onDialExhausted,
          })
        } else if (options?.url) {
          playDialToneUrl(options.url, { loop: true })
        } else {
          playDialTone()
        }
      }

      if (isNativeApp()) {
        if (mode === 'incoming') callAudioRouteArbiter.setPhase('incoming-ringtone')
        else if (mode === 'connecting') callAudioRouteArbiter.setPhase('connecting')
        else callAudioRouteArbiter.setPhase('outgoing-ringback')
        const routeOn = callAudioRouteArbiter.isActive()
          ? callAudioRouteArbiter.desiredSpeakerOn()
          : speakerOn
        void prepareNativeRingback(routeOn).then(ring, ring)
        return
      }
      ring()
    },
    [clearVibrateTimer],
  )

  useEffect(() => stop, [stop])

  return { start, stop }
}

/** Unlock HTML5 + Web Audio on first user interaction (required on iOS/Android PWA). */
export function useUnlockAudioOnGesture(): void {
  useEffect(() => {
    unlockAudioOnGesture()
  }, [])
}
